import React from 'react';
import { getCalAvatar } from '../config/calAvatars';
import {
  Drawer,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  Badge,
  alpha,
  useTheme,
  Collapse,
} from '@mui/material';
import {
  CloudUpload,
  History,
  Settings,
  HelpOutline,
  ChevronLeft,
  ChevronRight,
  Dashboard,
  Gavel,
  ExpandLess,
  ExpandMore,
  VerifiedUser,
  AttachMoney,
  Timeline,
  ShowChart,
  FolderOpen,
  NotificationsActive,
  Balance,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useCapabilities } from '../contexts/CapabilitiesContext';

interface ModernSidebarProps {
  open: boolean;
  onToggle: () => void;
  selectedTab: string;
  onTabChange: (tab: string) => void;
  uploadCount?: number;
}

// RBAC (RBAC_PLAN.md §9/§11): `cap` = the capability that gates the item; `null` = always
// visible (baseline). Filtering is applied via `can()` below — but during Phase 1 `can()`
// returns true for everything, so nothing is hidden yet. Phase 2 enforcement flips the flag.
const menuItems: any[] = [
  { id: 'dashboard', label: 'Dashboard', icon: Dashboard, cap: null },
  { id: 'meter-control-charts', label: 'Meter Factor Control Charts', icon: ShowChart, cap: null },
  { id: 'cost-compliance', label: 'Cost of Non-Compliance', icon: AttachMoney, cap: null },
  // Regression Utility is HIDDEN from the sidebar (9 Oct 2026, Travis). The screen, its route in
  // App.tsx and the `use_regression_utility` gate on /regression-utility/analyze and /regression/*
  // are all intact -- this only removes the nav entry, so it is a one-line restore.
  // { id: 'regression-utility', label: 'Regression Utility', icon: Timeline, cap: 'use_regression_utility' },
  { id: 'mass-balance', label: 'Mass Balance Utility', icon: Balance, cap: 'use_mass_balance' },
  { id: 'notifications', label: 'Notification Center', icon: NotificationsActive, cap: null }, // view is baseline; digest writes gated in-screen
  { id: 'compliance', label: 'Compliance', icon: Gavel, cap: 'use_cal' },
  {
    id: 'document-center', label: 'Document Center', icon: FolderOpen, cap: null,
    children: [
      { id: 'upload', label: 'Upload Documents', icon: CloudUpload, cap: 'upload_documents' },
      { id: 'history', label: 'Document History', icon: History, cap: null },
      { id: 'verification', label: 'Verification Queue', icon: VerifiedUser, cap: 'review_reports' },
    ],
  },
];

const ModernSidebar: React.FC<ModernSidebarProps> = ({
  open,
  onToggle,
  selectedTab,
  onTabChange,
  uploadCount = 0,
}) => {
  const theme = useTheme();
  const drawerWidth = open ? 260 : 72;
  // Which expandable groups (e.g. Document Center) are open.
  const [openGroups, setOpenGroups] = React.useState<Record<string, boolean>>({});

  // RBAC gating (Phase 1: `can()` returns true for everything, so this is a no-op filter
  // today; Phase 2 hides items whose capability the user lacks). Groups with no visible
  // children are dropped.
  const { can } = useCapabilities();
  const visibleItems = menuItems
    .filter((item) => !item.cap || can(item.cap))
    .map((item) =>
      Array.isArray(item.children)
        ? { ...item, children: item.children.filter((c: any) => !c.cap || can(c.cap)) }
        : item
    )
    .filter((item) => !Array.isArray(item.children) || item.children.length > 0);

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          borderRight: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          backdropFilter: 'blur(20px)',
        },
      }}
    >
      {/* Logo Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: open ? 'space-between' : 'center',
          p: 2,
          minHeight: 80,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <AnimatePresence>
          {!open && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.2 }}
            >
              <img
                src={getCalAvatar().path}
                alt="Cal Logo"
                style={{
                  height: '80px',
                  width: '80px',
                  objectFit: 'contain',
                  display: 'block',
                  borderRadius: '50%',
                  backgroundColor: '#f5f5f5',
                  padding: '4px'
                }}
              />
            </motion.div>
          )}
          {open && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              style={{ flex: 1 }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {/* CAL with logo */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <img
                    src={getCalAvatar().path}
                    alt="Cal Logo"
                    style={{
                      height: '70px',
                      width: '70px',
                      objectFit: 'contain',
                      borderRadius: '50%',
                      backgroundColor: '#f5f5f5',
                      padding: '3px'
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: '"Bustani", "Helvetica Neue", "Helvetica", -apple-system, sans-serif',
                      fontWeight: 400,
                      fontSize: '1.8rem',
                      letterSpacing: '3px',
                      color: theme.palette.text.primary,
                    }}
                  >
                    CAL
                  </Typography>
                </Box>
                {/* by Trindent */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pl: 5.5 }}>
                  <Typography
                    sx={{
                      fontSize: '1.4rem',
                      color: theme.palette.text.secondary,
                      fontStyle: 'italic'
                    }}
                  >
                    by
                  </Typography>
                  <img
                    src="/trindent-logo-h.png"
                    alt="Trindent"
                    style={{
                      height: '48px',
                      width: 'auto',
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>
        <IconButton
          onClick={onToggle}
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
            },
          }}
        >
          {open ? <ChevronLeft /> : <ChevronRight />}
        </IconButton>
      </Box>

      {/* Navigation Items */}
      <List sx={{ px: 1, py: 2 }}>
        {visibleItems.map((item) => {
          const isGroup = Array.isArray(item.children) && item.children.length > 0;
          const isSelected = selectedTab === item.id;
          const childSelected = isGroup && item.children.some((c: any) => c.id === selectedTab);
          const groupOpen = isGroup && (openGroups[item.id] || childSelected);
          const hasUploadBadge = item.id === 'upload' && uploadCount > 0;

          return (
            <React.Fragment key={item.id}>
              <Tooltip title={!open ? item.label : ''} placement="right">
                <ListItemButton
                  onClick={() => {
                    if (isGroup) {
                      if (!open) { onToggle(); }
                      setOpenGroups((prev) => ({ ...prev, [item.id]: !groupOpen }));
                    } else {
                      onTabChange(item.id);
                    }
                  }}
                  sx={{
                    borderRadius: '8px',
                    mb: 0.5,
                    position: 'relative',
                    backgroundColor: (isSelected || childSelected)
                      ? alpha(theme.palette.primary.main, 0.08)
                      : 'transparent',
                    '&:hover': {
                      backgroundColor: (isSelected || childSelected)
                        ? alpha(theme.palette.primary.main, 0.12)
                        : alpha(theme.palette.text.primary, 0.04),
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 3,
                      height: isSelected ? 24 : 0,
                      borderRadius: '0 3px 3px 0',
                      backgroundColor: theme.palette.primary.main,
                      transition: 'height 0.2s ease',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: open ? 40 : 'auto',
                      color: (isSelected || childSelected) ? theme.palette.primary.main : theme.palette.text.secondary,
                    }}
                  >
                    <Badge
                      badgeContent={hasUploadBadge ? uploadCount : 0}
                      color="error"
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.65rem',
                          height: 16,
                          minWidth: 16,
                        },
                      }}
                    >
                      <item.icon />
                    </Badge>
                  </ListItemIcon>
                  {open && (
                    <>
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: '0.875rem',
                          fontWeight: (isSelected || childSelected) ? 600 : 400,
                          color: (isSelected || childSelected) ? theme.palette.primary.main : theme.palette.text.primary,
                        }}
                      />
                      {isGroup && (groupOpen ? <ExpandLess /> : <ExpandMore />)}
                    </>
                  )}
                </ListItemButton>
              </Tooltip>

              {/* Group children (e.g. Document Center) */}
              {isGroup && open && (
                <Collapse in={groupOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 1.5 }}>
                    {item.children.map((subItem: any) => {
                      const isSubSelected = selectedTab === subItem.id;
                      const subBadge = subItem.id === 'upload' && uploadCount > 0 ? uploadCount : 0;
                      return (
                        <ListItemButton
                          key={subItem.id}
                          onClick={() => onTabChange(subItem.id)}
                          sx={{
                            borderRadius: '6px',
                            mb: 0.5,
                            py: 0.75,
                            backgroundColor: isSubSelected
                              ? alpha(theme.palette.primary.main, 0.08)
                              : 'transparent',
                            '&:hover': {
                              backgroundColor: isSubSelected
                                ? alpha(theme.palette.primary.main, 0.12)
                                : alpha(theme.palette.text.primary, 0.04),
                            },
                          }}
                        >
                          <ListItemIcon sx={{
                            minWidth: 34,
                            color: isSubSelected ? theme.palette.primary.main : theme.palette.text.secondary,
                          }}>
                            <Badge badgeContent={subBadge} color="error"
                              sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 15, minWidth: 15 } }}>
                              <subItem.icon fontSize="small" />
                            </Badge>
                          </ListItemIcon>
                          <ListItemText
                            primary={subItem.label}
                            primaryTypographyProps={{
                              fontSize: '0.8125rem',
                              fontWeight: isSubSelected ? 600 : 400,
                              color: isSubSelected
                                ? theme.palette.primary.main
                                : theme.palette.text.secondary,
                            }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>

      {/* Bottom Section */}
      <Box sx={{ mt: 'auto', p: 1 }}>
        <Divider sx={{ mb: 1 }} />
        <List>
          <Tooltip title={!open ? 'Settings' : ''} placement="right">
            <ListItemButton
              onClick={() => onTabChange('settings')}
              sx={{
                borderRadius: '8px',
                mb: 0.5,
              }}
            >
              <ListItemIcon sx={{ minWidth: open ? 40 : 'auto' }}>
                <Settings fontSize="small" />
              </ListItemIcon>
              {open && (
                <ListItemText
                  primary="Settings"
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
              )}
            </ListItemButton>
          </Tooltip>
          <Tooltip title={!open ? 'Help' : ''} placement="right">
            <ListItemButton
              onClick={() => onTabChange('help')}
              sx={{
                borderRadius: '8px',
              }}
            >
              <ListItemIcon sx={{ minWidth: open ? 40 : 'auto' }}>
                <HelpOutline fontSize="small" />
              </ListItemIcon>
              {open && (
                <ListItemText
                  primary="Help"
                  primaryTypographyProps={{ fontSize: '0.875rem' }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </List>
      </Box>
    </Drawer>
  );
};

export default ModernSidebar;